import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, InputGroup, Alert, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { getEvaluationReportStatusVariant, getEvaluationReportStatusDisplayText } from '../utils/evaluationStatus';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../utils/apiUtils';
import { canEditItem } from '../utils/permissionUtils';
import EvaluationReportModal from './EvaluationReportModal';
import './Evaluations.css';

const Evaluations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [evaluations, setEvaluations] = useState([]);
  const [evaluationReports, setEvaluationReports] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [editingReportId, setEditingReportId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  
  // Normalize API statuses for evaluation reports
  const normalizeReportStatus = (status) => {
    if (!status) return 'Pending';
    const s = String(status).trim().toLowerCase();
    if (s === 'submitted' || s === 'submit' || s === 'created') return 'Pending';
    return status;
  };

  // Check if user has permission to view evaluations
  const canViewEvaluations = user && ['Extension Coordinator', 'Extension Head', 'GAD', 'Vice Chancellor for Administration and Finance', 'Vice Chancellor for Research, Development and Extension Services', 'Chancellor', 'Admin'].includes(user.role);
  const canCreateEvaluationReports = user && ['Extension Coordinator','Extension Head','GAD','Vice Chancellor for Administration and Finance','Vice Chancellor for Research, Development and Extension Services','Chancellor','Admin'].includes(user.role);
  const isExtensionCoordinator = user && user.role === 'Extension Coordinator';

  useEffect(() => {
    if (!canViewEvaluations) {
      navigate('/dashboard');
      return;
    }

    fetchAllData();
  }, [canViewEvaluations, navigate]);

  // Combine evaluations and evaluation reports
  useEffect(() => {
    const combined = [
      ...evaluations.map(evaluation => ({ ...evaluation, type: 'evaluation' })),
      ...evaluationReports.map(report => ({ ...report, type: 'report', status: normalizeReportStatus(report.status) }))
    ];
    setCombinedData(combined);
  }, [evaluations, evaluationReports]);

  // Filter combined data based on search term and status
  useEffect(() => {
    let filtered = combinedData;

    // Filter by search term (project title, evaluator name, or created by name)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.project_title?.toLowerCase().includes(term) ||
        item.evaluator_name?.toLowerCase().includes(term) ||
        item.created_by_name?.toLowerCase().includes(term)
      );
    }

    // Filter by evaluation report status (applies only to report items)
    if (statusFilter) {
      filtered = filtered.filter(item => {
        if (item.type !== 'report') return true;
        const normalized = normalizeReportStatus(item.status);
        return String(normalized).toLowerCase() === String(statusFilter).toLowerCase();
      });
    }

    setFilteredData(filtered);
  }, [combinedData, searchTerm, statusFilter]);

  // Available statuses derived from current report data
  const availableReportStatuses = Array.from(new Set(
    evaluationReports.map(r => normalizeReportStatus(r.status)).filter(Boolean)
  ));

  const fetchEvaluations = async () => {
    try {
      const { data } = await apiClient.get(API_ENDPOINTS.EVALUATIONS);
      setEvaluations(data);
    } catch (err) {
      console.error('Failed to load evaluations', err);
      setError('Failed to load evaluations');
    }
  };

  const fetchEvaluationReports = async () => {
    try {
      const { data } = await apiClient.get(API_ENDPOINTS.REPORTS);
      setEvaluationReports(data);
    } catch (err) {
      console.error('Failed to load evaluation reports', err);
      setError('Failed to load evaluation reports');
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchEvaluations(),
        fetchEvaluationReports(),
        fetchStats()
      ]);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await apiClient.get(`${API_ENDPOINTS.EVALUATIONS}/stats/overview`);
      setStats(data);
    } catch (err) {
      console.error('Failed to load evaluation statistics', err);
    }
  };

  const getDecisionVariant = (decision) => {
    switch (decision) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Needs Revision':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'Approved':
        return 'bi-check-circle-fill';
      case 'Rejected':
        return 'bi-x-circle-fill';
      case 'Needs Revision':
        return 'bi-exclamation-triangle-fill';
      default:
        return 'bi-question-circle-fill';
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const hasActiveSearch = searchTerm.trim();

  if (!canViewEvaluations) {
    return null;
  }

  return (
    <div className="evaluations-container">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="mb-1">Evaluation Reports</h3>
            </div>
            {canCreateEvaluationReports && (
              <Button 
                variant="success"
                onClick={() => {
                  setEditingReportId(null);
                  setShowReportModal(true);
                }}
              >
                <i className="bi bi-clipboard-data me-2"></i>
                Create Evaluation Report
              </Button>
            )}
          </div>



          {/* Search Section */}
          <Card className="shadow-sm mb-3 search-filter-card">
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Search Evaluations</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="bi bi-search"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search by project title, evaluator name, or report creator..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Filter by Status</Form.Label>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      {availableReportStatuses.map(status => (
                        <option key={status} value={status}>{getEvaluationReportStatusDisplayText(status)}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <div className="d-flex w-100 gap-2">
                    <Button
                      variant="outline-secondary"
                      onClick={clearSearch}
                      disabled={!hasActiveSearch}
                      className="w-50"
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Clear Search
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setStatusFilter('')}
                      disabled={!statusFilter}
                      className="w-50"
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Clear Status
                    </Button>
                  </div>
                </Col>
              </Row>
              
              {/* Results Summary */}
              {hasActiveSearch && (
                <div className="mt-3 results-summary">
                  <small className="text-muted">
                    Showing {filteredData.length} of {combinedData.length} items
                    {searchTerm.trim() && ` matching "${searchTerm}"`}
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading evaluation reports...</p>
                </div>
              ) : filteredData.length === 0 ? (
                <div className="text-center py-4 empty-state">
                  <i className="bi bi-clipboard-x display-4 text-muted"></i>
                  <p className="mt-2 text-muted">
                    {hasActiveSearch 
                      ? 'No items match your search criteria. Try adjusting your search terms.'
                      : 'No evaluation reports found.'
                    }
                  </p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Evaluator</th>
                      <th>Status</th>
                      <th>Date Submitted</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item) => (
                      <tr key={`${item.type}-${item.eval_id || item.report_id}`}>
                        <td>
                          <div>
                            <span>{item.project_title}</span>
                            <br />
                            <small className="text-muted">ID: {item.project_id}</small>
                          </div>
                        </td>
                        <td>
                          <div>
                            <span>{item.type === 'evaluation' ? item.evaluator_name : item.created_by_name}</span>
                            <br />
                            <small className="text-muted">
                              {item.type === 'evaluation' ? item.evaluator_role : item.created_by_department}
                            </small>
                          </div>
                        </td>
                        <td>
                          {item.type === 'evaluation' ? (
                            <Badge bg={getDecisionVariant(item.decision)} className="decision-badge">
                              <i className={`${getDecisionIcon(item.decision)} me-1`}></i>
                              {item.decision}
                            </Badge>
                          ) : (
                            <Badge bg={getEvaluationReportStatusVariant(item.status || 'Pending')}>
                              {getEvaluationReportStatusDisplayText(item.status || 'Pending')}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <small>{new Date(item.created_at).toLocaleDateString()}</small>
                          <br />
                          <small className="text-muted">{new Date(item.created_at).toLocaleTimeString()}</small>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            {item.type === 'evaluation' ? (
                              <>
                                <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-view-${item.eval_id}`}>View</Tooltip>}>
                                  <Button
                                    as={Link}
                                    to={`/evaluations/${item.eval_id}`}
                                    variant="link"
                                    className="icon-only-btn icon-view"
                                    aria-label="View evaluation"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </Button>
                                </OverlayTrigger>

                                {(user.user_id === item.evaluator_id || user.role === 'Admin') && (
                                  <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-edit-${item.eval_id}`}>Edit</Tooltip>}>
                                    <Button
                                      as={Link}
                                      to={`/evaluations/${item.eval_id}/edit`}
                                      variant="link"
                                      className="icon-only-btn icon-edit"
                                      aria-label="Edit evaluation"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </Button>
                                  </OverlayTrigger>
                                )}

                                {user.role === 'Admin' && (
                                  <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-del-${item.eval_id}`}>Delete</Tooltip>}>
                                    <Button
                                      variant="link"
                                      className="icon-only-btn icon-delete"
                                      aria-label="Delete evaluation"
                                      onClick={async () => {
                                        if (!window.confirm('Are you sure you want to delete this evaluation?')) return;
                                        try {
                                          await apiClient.delete(`${API_ENDPOINTS.EVALUATIONS}/${item.eval_id}`);
                                          setEvaluations(prev => prev.filter(e => e.eval_id !== item.eval_id));
                                        } catch (err) {
                                          console.error('Failed to delete evaluation', err);
                                          setError('Failed to delete evaluation');
                                        }
                                      }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </Button>
                                  </OverlayTrigger>
                                )}
                              </>
                            ) : (
                              <>
                                <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-view-report-${item.report_id}`}>View Report</Tooltip>}>
                                  <Button
                                    as={Link}
                                    to={`/reports/${item.report_id}`}
                                    variant="link"
                                    className="icon-only-btn icon-view"
                                    aria-label="View report"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </Button>
                                </OverlayTrigger>

                                {canCreateEvaluationReports && canEditItem(user, item, 'report').canEdit && (
                                  <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-edit-report-${item.report_id}`}>Edit Report</Tooltip>}>
                                    <Button
                                      variant="link"
                                      className="icon-only-btn icon-edit"
                                      aria-label="Edit report"
                                      onClick={() => {
                                        setEditingReportId(item.report_id);
                                        setShowReportModal(true);
                                      }}
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </Button>
                                  </OverlayTrigger>
                                )}

                                {canCreateEvaluationReports && !isExtensionCoordinator && (
                                  <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-del-report-${item.report_id}`}>Delete Report</Tooltip>}>
                                    <Button
                                      variant="link"
                                      className="icon-only-btn icon-delete"
                                      aria-label="Delete report"
                                      onClick={() => {
                                        setReportToDelete(item);
                                        setShowDeleteModal(true);
                                      }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </Button>
                                  </OverlayTrigger>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Evaluation Report Modal */}
      <EvaluationReportModal
        show={showReportModal}
        onHide={() => {
          setShowReportModal(false);
          setEditingReportId(null);
        }}
        reportId={editingReportId}
        onSuccess={() => {
          fetchAllData();
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => {
        setShowDeleteModal(false);
        setReportToDelete(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Delete Evaluation Report?</h5>
            <p className="text-muted">
              Are you sure you want to delete this evaluation report? This action cannot be undone.
              {reportToDelete && (
                <div className="mt-2">
                  <strong>Report ID:</strong> {reportToDelete.report_id}
                  <br />
                  <strong>Project:</strong> {reportToDelete.project_title}
                </div>
              )}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDeleteModal(false);
            setReportToDelete(null);
          }}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={async () => {
              if (!reportToDelete) return;
              try {
                await apiClient.delete(`${API_ENDPOINTS.REPORTS}/${reportToDelete.report_id}`);
                setEvaluationReports(prev => prev.filter(r => r.report_id !== reportToDelete.report_id));
                setShowDeleteModal(false);
                setReportToDelete(null);
                setError('');
                // Show success message
                const successAlert = document.createElement('div');
                successAlert.className = 'alert alert-success alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3';
                successAlert.style.zIndex = '9999';
                successAlert.innerHTML = `
                  <i class="bi bi-check-circle me-2"></i>
                  Evaluation report deleted successfully!
                  <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
                `;
                document.body.appendChild(successAlert);
                setTimeout(() => {
                  successAlert.remove();
                }, 3000);
              } catch (err) {
                console.error('Failed to delete evaluation report', err);
                setError('Failed to delete evaluation report');
                setShowDeleteModal(false);
                setReportToDelete(null);
              }
            }}
          >
            <i className="bi bi-trash me-2"></i>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Evaluations;
